/**
 * Mode detection: free vs cloud.
 *
 * `cloud.bliksys.com` = paid surface (DB-backed). Everything else
 * (including `bliksys.com` and self-hosted previews on localhost)
 * defaults to free mode. The cloud subdomain is hardcoded — see
 * Guardrail 4 in docs/paid-plan.md ("Bliksys is never self-hostable").
 *
 * Pure function so tests can pass any host string. The runtime calls
 * it with `window.location.host` at app init.
 */

export type AppMode = 'free' | 'cloud';

const CLOUD_HOSTS: readonly string[] = ['cloud.bliksys.com'];

export function detectMode(host: string): AppMode {
  // Exact-match production cloud host first.
  if (CLOUD_HOSTS.includes(host)) return 'cloud';

  // Local dev: any localhost port that's been explicitly flagged via
  // `?cloud` query param. Catch-all-localhost would conflict with the
  // free dev workflow that runs on the same host. Lazily expanded
  // when the cloud web shell ships its own dev port.
  if ((host.startsWith('localhost:') || host === 'localhost') && hasCloudFlag()) {
    return 'cloud';
  }

  return 'free';
}

function hasCloudFlag(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return new URL(window.location.href).searchParams.has('cloud');
  } catch {
    return false;
  }
}
